<script lang="ts">
	import type { HolsterAccessoryToSelectedAttributes, AllHolsterAccessoryAttributes } from 'shared/bzns/holster/accessories'
	import type { Accessory } from './AccessorySelectorLine.svelte'

	import AccessorySelectorGroup from './AccessorySelectorGroup.svelte'
	import { writable, type Writable } from 'svelte/store'

	export let holster_line_image_file_name_string: TrexHolsterLine[`image_file_name_string`]
	export let holster_line_image_position_algorithm: TrexHolsterLine[`image_position_algorithm`]
	export let accessory_groups: Accessory[][] = []
	export let accessories_with_purchasability: Accessory[]
	export let holster_accessory_to_attributes: { [holster_accessory_ref: number]: AllHolsterAccessoryAttributes }
	export let holster_accessory_to_selected_attributes: HolsterAccessoryToSelectedAttributes
	export let promo_active: boolean
	export let selected_holster_properties: Writable<{ [key: string]: unknown }> = writable()

	export let selected_accessory_refs = new Set()
	export let holster_accessory_ref_to_color_ref: Map<number, number>

	$: groups_of_attachments = accessory_groups.filter(accessories => accessories.some(accessory => accessory.sibling_variation_may_be_attached_to_holster))
	$: groups_of_combos = accessory_groups.filter(accessories => accessories.every(accessory => !accessory.sibling_variation_may_be_attached_to_holster && accessory.enable_during_holster_promo))
	$: groups_of_accessories = accessory_groups.filter(accessories => accessories.some(accessory => !accessory.sibling_variation_may_be_attached_to_holster && !accessory.enable_during_holster_promo))

	const toggle_selection = (accessories: Accessory[]) => {
		accessories.forEach(accessory => {
			const { holster_accessory_ref, include_one_with_holster, purchasable } = accessory

			if (include_one_with_holster || !purchasable) {
				return
			}

			selected_accessory_refs.has(holster_accessory_ref)
				? selected_accessory_refs.delete(holster_accessory_ref)
				: selected_accessory_refs.add(holster_accessory_ref)
		})
		selected_accessory_refs = selected_accessory_refs
	}
</script>

{#if groups_of_attachments.length}
	<div class=accessory_selector_title>Attachments</div>

	<div class="accessory_selector">
		{#each groups_of_attachments as accessories (accessories.map(accessory => accessory.holster_accessory_ref).join(`,`))}
			<div class=accessory_container>
				<AccessorySelectorGroup
					{accessories}
					{accessories_with_purchasability}
					{holster_accessory_to_attributes}
					bind:holster_accessory_to_selected_attributes
					on:toggle={({ detail: accessories }) => toggle_selection(accessories)}
					bind:holster_accessory_ref_to_color_ref
					{holster_line_image_file_name_string}
					{holster_line_image_position_algorithm}
					{promo_active}
					{selected_holster_properties}
					checked={accessories.every(accessory => selected_accessory_refs.has(accessory.holster_accessory_ref))}
				/>
			</div>
		{/each}
	</div>
{/if}

{#if groups_of_combos.length}
	<div class=accessory_selector_title>Combos</div>

	<div class="accessory_selector">
		{#each groups_of_combos as accessories (accessories.map(accessory => accessory.holster_accessory_ref).join(`,`))}
			<div class=accessory_container>
				<AccessorySelectorGroup
					{accessories}
					{accessories_with_purchasability}
					{holster_accessory_to_attributes}
					bind:holster_accessory_to_selected_attributes
					on:toggle={({ detail: accessories }) => toggle_selection(accessories)}
					bind:holster_accessory_ref_to_color_ref
					{holster_line_image_file_name_string}
					{holster_line_image_position_algorithm}
					{promo_active}
					{selected_holster_properties}
					checked={accessories.every(accessory => selected_accessory_refs.has(accessory.holster_accessory_ref))}
				/>
			</div>
		{/each}
	</div>
{/if}

{#if groups_of_accessories.length}
	<div class=accessory_selector_title>Accessories</div>

	<div class="accessory_selector">
		{#each groups_of_accessories as accessories (accessories.map(accessory => accessory.holster_accessory_ref).join(`,`))}
			<div class=accessory_container>
				<AccessorySelectorGroup
					{accessories}
					{accessories_with_purchasability}
					{holster_accessory_to_attributes}
					bind:holster_accessory_to_selected_attributes
					on:toggle={({ detail: accessories }) => toggle_selection(accessories)}
					bind:holster_accessory_ref_to_color_ref
					{holster_line_image_file_name_string}
					{holster_line_image_position_algorithm}
					{promo_active}
					{selected_holster_properties}
					checked={accessories.every(accessory => selected_accessory_refs.has(accessory.holster_accessory_ref))}
				/>
			</div>
		{/each}
	</div>
{/if}

<style>
	.accessory_selector_title {
		letter-spacing: 3px;
		color: #999;
		text-transform: uppercase;
		text-align: center;
		padding-top: 8px;
	}

	.accessory_selector {
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		font-size: 14px;
		max-height: 400px;
		overflow-y: auto;
		border-radius: 8px;
		border: 1px solid var(--input-border-gray);
	}

	.accessory_container {
		border-bottom: 1px solid var(--input-border-gray);
	}
	.accessory_container:last-child {
		border-bottom: none;
	}

	@media (max-width: 420px) {
		.accessory_selector {
			font-size: 12px;
		}
	}
</style>
